"use client";

import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ImagePlus,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DialogHeading } from "@/components/ui/dialog-heading";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganizationBranding } from "@/contexts/OrganizationBrandingContext";
import { useToast } from "@/hooks/use-toast";
import {
  normalizeOrganizationBrandImage,
  type OrganizationBrandImageAdjustment,
} from "@/lib/organizationBrandImage";
import { cn } from "@/lib/utils";
import { localAuthRepository } from "@/repository/localAuthRepository";
import {
  ORGANIZATION_LOGO_CONTENT_TYPES,
  ORGANIZATION_LOGO_MAX_BYTES,
  organizationBrandingRepository,
  type OrganizationBrandAssetType,
  type OrganizationBranding,
} from "@/repository/organizationBrandingRepository";

const ACCEPTED_FILE_TYPES = ORGANIZATION_LOGO_CONTENT_TYPES.join(",");
const DEFAULT_ADJUSTMENT: OrganizationBrandImageAdjustment = {
  zoom: 1,
  offsetX: 0,
  offsetY: 0,
};

type OrganizationLogoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function OrganizationLogoDialog({
  open,
  onOpenChange,
}: OrganizationLogoDialogProps) {
  const { branding, error, isLoading, refresh, updateBranding } =
    useOrganizationBranding();
  const { toast } = useToast();
  const [deletingType, setDeletingType] =
    useState<OrganizationBrandAssetType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const canEdit =
    localAuthRepository.getUserInfo()?.role === "organization_admin";

  const deleteAsset = async () => {
    if (!deletingType || !canEdit) return;
    setIsDeleting(true);
    try {
      const next =
        await organizationBrandingRepository.deleteBrandAsset(deletingType);
      updateBranding(next);
      toast({
        title:
          deletingType === "icon"
            ? "組織アイコンを削除しました"
            : "横長ロゴを削除しました",
        variant: "success",
      });
      setDeletingType(null);
    } catch (cause) {
      console.error(cause);
      toast({
        title: "ブランド画像を削除できませんでした",
        description: "時間をおいて、もう一度お試しください。",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain sm:max-w-3xl">
          <DialogHeading
            icon={
              <span className="bg-brand-light text-brand-strong flex size-9 items-center justify-center rounded-xl">
                <ImagePlus className="size-4" aria-hidden="true" />
              </span>
            }
            title="組織のブランド画像を管理"
            description="アイコンと横長ロゴを、表示場所に合わせて登録します。"
          />

          {isLoading ? (
            <div
              className="text-muted-foreground flex min-h-64 items-center justify-center gap-2 text-sm"
              role="status"
            >
              <RefreshCw
                className="text-brand size-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              組織情報を読み込んでいます
            </div>
          ) : error || !branding ? (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/5 flex items-start gap-3 rounded-(--radius-container) border p-4"
            >
              <AlertTriangle
                className="text-destructive mt-0.5 size-5 shrink-0"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium">組織情報を読み込めませんでした</p>
                <p className="text-muted-foreground  text-sm">
                  通信状態を確認して、もう一度お試しください。
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => void refresh()}
                >
                  <RefreshCw aria-hidden="true" />
                  再読み込み
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <BrandAssetEditor
                assetType="icon"
                branding={branding}
                currentUrl={branding.iconUrl ?? branding.logoUrl}
                canEdit={canEdit}
                onSaved={updateBranding}
                onDelete={() => setDeletingType("icon")}
              />
              <BrandAssetEditor
                assetType="horizontal-logo"
                branding={branding}
                currentUrl={branding.horizontalLogoUrl}
                canEdit={canEdit}
                onSaved={updateBranding}
                onDelete={() => setDeletingType("horizontal-logo")}
              />
              {!canEdit ? (
                <p className="bg-muted text-muted-foreground rounded-(--radius-container) px-4 py-3 text-sm">
                  ブランド画像の変更は組織管理者のみ行えます。
                </p>
              ) : null}
              <div className="flex justify-end border-t pt-4">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  閉じる
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deletingType !== null}
        onOpenChange={(next) => !next && setDeletingType(null)}
        title={
          deletingType === "horizontal-logo"
            ? "横長ロゴを削除しますか？"
            : "組織アイコンを削除しますか？"
        }
        description="未登録の表示場所では、組織名または頭文字が代わりに表示されます。"
        confirmLabel="削除"
        onConfirm={() => void deleteAsset()}
        loading={isDeleting}
      />
    </>
  );
}

type BrandAssetEditorProps = {
  assetType: OrganizationBrandAssetType;
  branding: OrganizationBranding;
  currentUrl: string | null;
  canEdit: boolean;
  onSaved: (branding: OrganizationBranding) => void;
  onDelete: () => void;
};

function BrandAssetEditor({
  assetType,
  branding,
  currentUrl,
  canEdit,
  onSaved,
  onDelete,
}: BrandAssetEditorProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [adjustment, setAdjustment] = useState(DEFAULT_ADJUSTMENT);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isIcon = assetType === "icon";
  const title = isIcon ? "組織アイコン" : "組織ロゴ（横長）";
  const shape = isIcon ? "正方形 1:1" : "横長 3:1";
  const recommendedSize = isIcon ? "512×512px" : "1,200×400px";
  const displayLocations = isIcon
    ? ["Webヘッダー", "会員アプリのチャット一覧・詳細"]
    : ["会員アプリの所属組織", "スタッフアプリのプロフィール"];

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    setValidationError(null);
    setSelectedFile(null);
    if (!file) return;
    if (
      !ORGANIZATION_LOGO_CONTENT_TYPES.includes(
        file.type as (typeof ORGANIZATION_LOGO_CONTENT_TYPES)[number]
      )
    ) {
      setValidationError("PNG・JPEG・WebP形式の画像を選択してください");
      return;
    }
    if (file.size < 1 || file.size > ORGANIZATION_LOGO_MAX_BYTES) {
      setValidationError("画像サイズは2MB以内にしてください");
      return;
    }
    setAdjustment(DEFAULT_ADJUSTMENT);
    setSelectedFile(file);
  };

  const save = async () => {
    if (!selectedFile || !canEdit) return;
    setIsSaving(true);
    setValidationError(null);
    try {
      const normalized = await normalizeOrganizationBrandImage(
        selectedFile,
        assetType,
        adjustment
      );
      const upload = await organizationBrandingRepository.uploadBrandAsset(
        assetType,
        normalized
      );
      const next = await organizationBrandingRepository.saveBrandAsset(
        assetType,
        upload.key
      );
      onSaved(next);
      setSelectedFile(null);
      setAdjustment(DEFAULT_ADJUSTMENT);
      toast({
        title: `${title}を更新しました`,
        description: "ウェブと各モバイルアプリの表示に反映されます。",
        variant: "success",
      });
    } catch (cause) {
      console.error(cause);
      setValidationError(
        cause instanceof Error
          ? cause.message
          : `${title}を更新できませんでした`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const displayUrl = previewUrl ?? currentUrl;
  const transform = previewUrl
    ? `translate(${adjustment.offsetX * 10}%, ${adjustment.offsetY * 10}%) scale(${adjustment.zoom})`
    : undefined;

  return (
    <section className="bg-muted/20 rounded-(--radius-container) border p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{title}</h3>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                currentUrl
                  ? "bg-brand-light text-brand-strong"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {currentUrl ? "登録済み" : "未登録"}
            </span>
          </div>
        </div>

        {currentUrl && canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="text-muted-foreground size-10 shrink-0"
                disabled={isSaving}
                aria-label={`${title}の操作メニューを開く`}
              >
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                <Trash2 aria-hidden="true" />
                削除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <div className="mt-4 grid gap-5 sm:grid-cols-[16rem_minmax(0,1fr)] sm:items-start">
        <div className="min-w-0">
          <p className="text-muted-foreground text-xs font-medium">
            現在の画像
          </p>
          <div className="mt-2 flex w-full justify-center">
            <BrandCanvas
              assetType={assetType}
              imageUrl={displayUrl}
              organizationName={branding.name}
              transform={transform}
              showSafeArea={previewUrl !== null}
            />
          </div>
          <p className="text-muted-foreground mt-2 text-center text-xs">
            <span className="text-foreground font-medium">{shape}</span>
            <span aria-hidden="true"> ・ </span>
            推奨 {recommendedSize}
          </p>
        </div>

        <div className="min-w-0 space-y-4">
          <div>
            <p className="text-muted-foreground text-xs font-medium">
              表示される場所
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {displayLocations.map((location) => (
                <span
                  key={location}
                  className="bg-background text-foreground rounded-full border px-2.5 py-1 text-xs"
                >
                  {location}
                </span>
              ))}
            </div>
          </div>

          <div className="border-t pt-3">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={ACCEPTED_FILE_TYPES}
              onChange={selectFile}
              disabled={!canEdit || isSaving}
              aria-hidden="true"
              tabIndex={-1}
            />
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canEdit || isSaving}
            >
              <Upload aria-hidden="true" />
              {currentUrl ? "画像を変更" : "画像を登録"}
            </Button>
            <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
              PNG／JPEG／WebP・2MB以内
            </p>
          </div>

          {selectedFile ? (
            <p
              className="text-brand-strong break-all text-sm"
              aria-live="polite"
            >
              {selectedFile.name}（
              {Math.max(1, Math.round(selectedFile.size / 1024))}KB）
            </p>
          ) : null}
          {selectedFile ? (
            <ImageAdjustmentControls
              value={adjustment}
              onChange={setAdjustment}
              disabled={isSaving}
            />
          ) : null}
          {validationError ? (
            <p
              className="text-destructive text-sm"
              role="alert"
              aria-live="assertive"
            >
              {validationError}
            </p>
          ) : null}
          {selectedFile ? (
            <Button
              className="min-h-11"
              onClick={() => void save()}
              disabled={!canEdit}
              isLoading={isSaving}
            >
              <CheckCircle2 aria-hidden="true" />
              この画像を保存
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function BrandCanvas({
  assetType,
  imageUrl,
  organizationName,
  transform,
  showSafeArea,
}: {
  assetType: OrganizationBrandAssetType;
  imageUrl: string | null;
  organizationName: string;
  transform?: string;
  showSafeArea: boolean;
}) {
  const isIcon = assetType === "icon";
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const imageUnavailable = imageUrl !== null && failedUrl === imageUrl;
  return (
    <div
      className={cn(
        "bg-background relative flex shrink-0 items-center justify-center overflow-hidden border shadow-sm",
        isIcon ? "size-36 rounded-2xl" : "aspect-[3/1] w-full sm:w-64"
      )}
    >
      {imageUrl && !imageUnavailable ? (
        <div className={cn("size-full", isIcon ? "p-[12%]" : "p-[8%]")}>
          <img
            src={imageUrl}
            alt={`${organizationName}の${isIcon ? "組織アイコン" : "横長ロゴ"}プレビュー`}
            width={isIcon ? 512 : 1200}
            height={isIcon ? 512 : 400}
            className="size-full object-contain transition-transform duration-200 motion-reduce:transition-none"
            style={{ transform }}
            onError={() => setFailedUrl(imageUrl)}
          />
        </div>
      ) : (
        <div className="text-muted-foreground flex flex-col items-center gap-2 text-xs">
          <ImagePlus className="size-7" aria-hidden="true" />
          {imageUnavailable ? "画像を表示できません" : "未登録"}
        </div>
      )}
      {showSafeArea ? (
        <div
          className={cn(
            "pointer-events-none absolute border border-dashed border-blue-500/70",
            isIcon ? "inset-[12%] rounded-full" : "inset-[8%]"
          )}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

function ImageAdjustmentControls({
  value,
  onChange,
  disabled,
}: {
  value: OrganizationBrandImageAdjustment;
  onChange: (value: OrganizationBrandImageAdjustment) => void;
  disabled: boolean;
}) {
  const fields = [
    { key: "zoom" as const, label: "大きさ", min: 0.8, max: 1.2, step: 0.01 },
    { key: "offsetX" as const, label: "左右", min: -1, max: 1, step: 0.01 },
    { key: "offsetY" as const, label: "上下", min: -1, max: 1, step: 0.01 },
  ];
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-xs font-medium">表示位置を調整</legend>
      {fields.map((field) => (
        <label
          key={field.key}
          className="grid grid-cols-[2.5rem_1fr] items-center gap-2 text-xs"
        >
          <span className="text-muted-foreground">{field.label}</span>
          <input
            type="range"
            min={field.min}
            max={field.max}
            step={field.step}
            value={value[field.key]}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...value, [field.key]: Number(event.target.value) })
            }
            className="accent-brand w-full"
          />
        </label>
      ))}
    </fieldset>
  );
}
